import AvailableDomainsGrid from "components/availableDomainsGrid/AvailableDomainsGrid";
import InfoText from "components/infoText/InfoText";
import Page from "components/page/Page";
import PageTitle from "components/pageTitle/PageTitle";
import PageWrapper from "components/pageWrapper/PageWrapper";
import DomainSearchBox, {
  isValidDomain,
} from "components/domainSearchBox/DomainSearchBox";
import { useAppDispatch, useAppSelector } from "hooks";
import { memo, useEffect, useMemo, useCallback, useState } from "react";
import { Alert } from "react-bootstrap";
import Helmet from "components/headHelmet/HeadHelmet";
import { useSearchParams } from "react-router-dom";
import styled from "styled-components";
import domainSlice from "reducers/domainSlice";
import {
  gettingAvailableDomainsSelector,
  availableDomainsSelector,
  getAvailableDomainsErrorSelector,
  availableDomainsTotalSelector,
  availableDomainsPageSelector,
  availableDomainsPageSizeSelector,
  resellersSelector,
} from "selectors/domainSelectors";
import { RootState } from "store";
import { GridPageChangeEvent } from "@progress/kendo-react-grid";
import "@progress/kendo-theme-default/dist/all.css";
import { getSiteUrl, isPrerendering } from "utility/urlUtility";
import { useTranslation } from "react-i18next";

const SearchWrapper = styled.div`
  margin-bottom: 20px;
  width: 100%;
`;

const AvailableDomainsPage = () => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation("available");
  const [searchParams, setSearchParams] = useSearchParams();
  const [searchInput, setSearchInput] = useState(
    searchParams.get("search") || "",
  );

  const gettingAvailableDomains = useAppSelector((state: RootState) =>
    gettingAvailableDomainsSelector(state),
  );

  const availableDomains = useAppSelector((state: RootState) =>
    availableDomainsSelector(state),
  );

  const total = useAppSelector((state: RootState) =>
    availableDomainsTotalSelector(state),
  );

  const page = useAppSelector((state: RootState) =>
    availableDomainsPageSelector(state),
  );

  const pageSize = useAppSelector((state: RootState) =>
    availableDomainsPageSizeSelector(state),
  );

  const getAvailableDomainsError = useAppSelector((state: RootState) =>
    getAvailableDomainsErrorSelector(state),
  );

  const resellers = useAppSelector((state: RootState) =>
    resellersSelector(state),
  );

  useEffect(() => {
    // Skip API calls during pre-rendering
    if (isPrerendering()) {
      return;
    }

    // Read page and search from URL
    const urlPage = parseInt(searchParams.get("page") || "1", 10);
    const validPage = urlPage > 0 ? urlPage : 1;
    const search = searchParams.get("search") || "";

    dispatch(
      domainSlice.actions.getAvailableDomains({ page: validPage, search }),
    );
  }, [dispatch, searchParams]);

  const handleSearchChange = useCallback(
    (value: string) => {
      setSearchInput(value);

      // Only filter if valid domain or empty
      if (value === "" || isValidDomain(value)) {
        const newParams: any = { page: "1" }; // Reset to page 1 on filter
        if (value) {
          newParams.search = value;
        }
        setSearchParams(newParams);
      }
    },
    [setSearchParams],
  );

  const handlePageChange = useCallback(
    (event: GridPageChangeEvent) => {
      const newPage = Math.floor(event.page.skip / event.page.take) + 1;

      // Update URL with new page number, preserve filter
      const newParams: any = { page: newPage.toString() };
      const search = searchParams.get("search");
      if (search) {
        newParams.search = search;
      }
      setSearchParams(newParams);
    },
    [searchParams, setSearchParams],
  );

  const pageUrl = useMemo(() => {
    const currentPage = parseInt(searchParams.get("page") || "1", 10);
    const baseUrl = `${getSiteUrl()}/available-domains`;
    return currentPage > 1 ? `${baseUrl}?page=${currentPage}` : baseUrl;
  }, [searchParams]);

  const totalPages = useMemo(() => {
    return Math.ceil(total / pageSize);
  }, [total, pageSize]);

  const currentPage = useMemo(() => {
    return parseInt(searchParams.get("page") || "1", 10);
  }, [searchParams]);

  const metaTitle = t("metaTitle");
  const metaDescription = t("metaDescription");

  return (
    <>
      <Helmet>
        <title>{metaTitle}</title>
        <meta name="description" content={metaDescription} />
        <meta name="robots" content="noindex, nofollow" />
        <link rel="canonical" href={pageUrl} />

        {/* Pagination meta tags for SEO */}
        {currentPage > 1 && (
          <link
            rel="prev"
            href={`${getSiteUrl()}/available-domains?page=${currentPage - 1}`}
          />
        )}
        {currentPage < totalPages && (
          <link
            rel="next"
            href={`${getSiteUrl()}/available-domains?page=${currentPage + 1}`}
          />
        )}

        {/* Open Graph / Facebook */}
        <meta property="og:type" content="website" />
        <meta property="og:url" content={pageUrl} />
        <meta property="og:title" content={metaTitle} />
        <meta property="og:description" content={metaDescription} />

        {/* Twitter */}
        <meta property="twitter:card" content="summary" />
        <meta property="twitter:url" content={pageUrl} />
        <meta property="twitter:title" content={metaTitle} />
        <meta property="twitter:description" content={metaDescription} />
      </Helmet>
      <Page collapsedView={true}>
        <PageWrapper>
          <PageTitle>{t("pageTitle")}</PageTitle>

          <InfoText>{t("infoText")}</InfoText>

          <SearchWrapper>
            <DomainSearchBox
              value={searchInput}
              onChange={handleSearchChange}
              loading={gettingAvailableDomains}
              debounceTimeout={300}
              fullWidth={true}
              placeholder={t("filterPlaceholder")}
            />
          </SearchWrapper>

          {getAvailableDomainsError && (
            <Alert variant="danger">{getAvailableDomainsError}</Alert>
          )}

          {!getAvailableDomainsError && (
            <AvailableDomainsGrid
              data={availableDomains || []}
              total={total}
              page={page}
              pageSize={pageSize}
              loading={gettingAvailableDomains}
              resellers={resellers || []}
              onPageChange={handlePageChange}
            />
          )}
        </PageWrapper>
      </Page>
    </>
  );
};

export default memo(AvailableDomainsPage);
